import { useState, useRef } from 'react';
import Modal from '../shared/Modal';
import { importBackgroundChecks } from '../../utils/api';

// Minimal CSV parser that handles quoted fields ("Smith, Jr." etc.).
function parseCsv(text) {
  const rows = [];
  let row = [], field = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQ = false;
      } else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some(f => f.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some(f => f.trim() !== '')) rows.push(row);
  return rows;
}

// Map the vendor CSV's header row to our fields (case-insensitive contains).
function findCol(headers, ...needles) {
  const lc = headers.map(h => h.trim().toLowerCase());
  for (const n of needles) {
    const idx = lc.findIndex(h => h === n);
    if (idx !== -1) return idx;
  }
  for (const n of needles) {
    const idx = lc.findIndex(h => h.includes(n));
    if (idx !== -1) return idx;
  }
  return -1;
}

export default function ImportBgChecksModal({ open, onClose, onImported, showToast }) {
  const [parsed, setParsed] = useState(null); // [{firstName,lastName,email,phone,status}]
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  function reset() {
    setParsed(null); setFileName(''); setParseError(''); setResult(null); setImporting(false);
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setParseError(''); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCsv(String(reader.result || ''));
        if (rows.length < 2) throw new Error('The file looks empty — no data rows found.');
        const headers = rows[0];
        const iFirst = findCol(headers, 'first name');
        const iLast = findCol(headers, 'last name');
        const iEmail = findCol(headers, 'primary email', 'email');
        const iPhone = findCol(headers, 'primary phone', 'phone');
        const iStatus = findCol(headers, 'status');
        if (iStatus === -1 || (iEmail === -1 && (iFirst === -1 || iLast === -1))) {
          throw new Error('Could not find the expected columns (First name, Last name, Primary Email, Status). Make sure this is the background-check export.');
        }
        const out = rows.slice(1).map(r => ({
          firstName: iFirst !== -1 ? (r[iFirst] || '').trim() : '',
          lastName: iLast !== -1 ? (r[iLast] || '').trim() : '',
          email: iEmail !== -1 ? (r[iEmail] || '').trim() : '',
          phone: iPhone !== -1 ? (r[iPhone] || '').trim() : '',
          status: (r[iStatus] || '').trim(),
        })).filter(r => r.status && (r.email || r.firstName || r.lastName));
        if (!out.length) throw new Error('No usable rows found in the file.');
        setParsed(out);
        setFileName(file.name);
      } catch (err) {
        setParsed(null); setFileName(file.name);
        setParseError(err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // allow re-selecting the same file
  }

  async function handleImport() {
    if (!parsed?.length) return;
    setImporting(true);
    try {
      const r = await importBackgroundChecks(parsed);
      setResult(r);
      showToast(`Background checks imported — ${r.updated} updated.`);
      if (onImported) onImported();
    } catch (err) {
      console.error('BG check import failed:', err);
      setParseError('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  }

  const clearedCount = parsed ? parsed.filter(r => /clear/i.test(r.status)).length : 0;

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Import Background Checks"
      footer={
        <>
          <button className="btn" onClick={() => { reset(); onClose(); }}>{result ? 'Done' : 'Cancel'}</button>
          {!result && (
            <button className="btn btn-dark" onClick={handleImport} disabled={!parsed?.length || importing}>
              {importing ? 'Importing…' : parsed ? `Import ${parsed.length} Rows` : 'Import'}
            </button>
          )}
        </>
      }
    >
      <div className="notice-bar" style={{ marginBottom: 16 }}>
        Upload the background-check CSV export (columns: First name, Last name, Primary Email, Primary Phone Number, Status).
        People are matched by email first, then by name. Re-uploading an updated file is safe — existing statuses are only
        changed when the file has something new.
      </div>

      {!result && (
        <div className="form-group">
          <button className="btn" onClick={() => fileRef.current?.click()}>
            {fileName ? 'Choose a Different File' : 'Choose CSV File'}
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleFile} />
          {fileName && !parseError && parsed && (
            <div style={{ marginTop: 10, fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--black)' }}>
              <strong style={{ fontFamily: 'var(--font-h)' }}>{fileName}</strong> — {parsed.length} rows found,{' '}
              {clearedCount} marked clear.
            </div>
          )}
          {parseError && (
            <div style={{ marginTop: 10, background: '#fff0f0', border: '1px solid #c00', borderRadius: 'var(--radius)', padding: '10px 14px', fontFamily: 'var(--font-b)', fontSize: 13, color: '#900' }}>
              {parseError}
            </div>
          )}
        </div>
      )}

      {result && (
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'var(--black)', lineHeight: 1.7 }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 800, marginBottom: 8 }}>Import complete</div>
          <div>• {result.updated} {result.updated === 1 ? 'person' : 'people'} updated with a new status</div>
          <div>• {result.unchanged} already up to date</div>
          <div>• {result.unmatchedCount} CSV {result.unmatchedCount === 1 ? 'row' : 'rows'} didn't match anyone in People</div>
          <div style={{ marginTop: 6, color: 'var(--muted)' }}>
            {result.peopleWithoutClearedCheck} {result.peopleWithoutClearedCheck === 1 ? 'person' : 'people'} in the app still have no cleared background check.
          </div>
          {result.unmatchedCount > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700 }}>
                Show unmatched rows {result.unmatchedCount > 100 ? '(first 100)' : ''}
              </summary>
              <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
                {(result.unmatched || []).map((u, i) => <div key={i}>{u}</div>)}
              </div>
            </details>
          )}
        </div>
      )}
    </Modal>
  );
}
